<template>
  <div class="heatmap-time-machine">
    <HeatmapTimeMachineControl
      :start-date="startDate"
      :start-time="startTimeStr"
      :duration="duration"
      :pv-data="pvData"
      :report-start-timestamp="playbackStartTimestamp"
      :basic-info="basicInfo"
      :disabled="!hasSelectedReport"
      :timezone-name="applyData.timezoneName"
      @segment-change="onSegmentChange"
      @frame-change="onFrameChange"
    />
    <div class="view_area_wrap">
      <div class="view_area"
            ref="container"
            :class="[`device-${device}`, { 'hidden-x': fitMode === 'width' || (fitMode !== 'width' && containerScale <= 1) }]">
        <div class="view"
             v-if="containerWidth > 0 && !isLoading"
             :style="containerStyle">
          <div class="view-heatmap">
            <canvas ref="heatmapCanvas"></canvas>
          </div>
        </div>
        <div v-if="isLoading" class="loading_indicator">
          <div class="loading circle">
            <svg x="0px" y="0px" viewBox="0 0 150 150">
              <circle class="loading-inner" cx="75" cy="75" r="60"/>
            </svg>
            <p class="loading_notice">{{ $t('common.loadingDot') }}</p>
          </div>
        </div>
      </div>
      <heatmap-dialog v-if="dialogType"
                      :dialogType="dialogType"
                      @onCloseClick="dialogType = null"/>
      <div class="view_options">
        <div class="fitMode" :class="fitMode">
          <a href="#"
             :class="{ on: fitMode === 'width', fit_to_width: true }"
             @click.prevent="onFitModeChange('width')">
            <i class="icon icon--fit-horizontal"></i>
          </a>
          <a href="#"
             :class="{ on: fitMode === 'height', fit_to_height: true }"
             @click.prevent="onFitModeChange('height')">
            <i class="icon icon--fit-vertical"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getCaptureImageUrl } from '@/api/report'
import HeatmapTimeMachineControl from '@/components/heatmap/HeatmapTimeMachineControl'
import HeatmapDialog from '@/components/heatmap/HeatmapDialog'
import {
  bucketMarkersByOpacity,
  shouldRenderFrame
} from '@/libs/heatmap-time-machine-renderer'

export default {
  name: 'HeatmapTimeMachine',
  components: {
    HeatmapTimeMachineControl,
    HeatmapDialog
  },
  props: {
    aid: {
      type: String,
      required: true
    },
    reportData: {
      type: Object,
      default: null
    },
    basicInfo: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      device: this.$route.query.device || (JSON.parse(localStorage.getItem('paramData')) || {}).device || 'desktop',
      captureWidth: 0,
      captureHeight: 0,
      containerWidth: 0,
      containerHeight: 0,
      containerScale: 1,
      imageUrl: null,
      isLoading: false,
      fitMode: 'width',
      svgData: null,
      dummyPvData: [],
      markers: [],
      animationFrameId: null,
      playbackSpeed: 1,
      lastRenderAt: null,
      dialogType: null
    }
  },
  computed: {
    ...mapState([
      'applyData',
      'captureImageTimestamp'
    ]),
    hasSelectedReport () {
      return this.reportData !== null
    },
    startDate () {
      return this.reportData ? this.reportData.startDate : ''
    },
    startTimeStr () {
      return this.reportData ? `${this.reportData.startHour}:${this.reportData.startMin}` : ''
    },
    duration () {
      return this.reportData ? this.reportData.duration : 5
    },
    pvData () {
      if (this.reportData) return this.reportData.pvData
      return this.dummyPvData
    },
    containerStyle () {
      return {
        width: `${this.containerWidth}px`,
        height: `${this.containerHeight}px`,
        backgroundImage: `url(${this.imageUrl})`
      }
    },
    captureImageParams () {
      const today = new Date().toISOString().slice(0, 10)
      const reportStartDate = this.toDateParam(this.reportData && this.reportData.startDate, today)
      const reportEndDate = this.toDateParam(this.reportData && this.reportData.endDate, today)
      return {
        aid: this.aid,
        device: this.device,
        endDate: reportEndDate,
        startDate: reportStartDate
      }
    },
    ratio () {
      if (this.captureWidth && this.captureHeight) {
        return this.captureWidth / this.captureHeight
      }
      return 0
    },
    frames () {
      if (!this.reportData || !this.reportData.replayData) return []
      return this.reportData.replayData.frames || []
    },
    playbackStartTimestamp () {
      return this.getPlaybackStartTimestamp()
    }
  },
  watch: {
    reportData (val) {
      this.markers = []
      this.lastRenderAt = null
      this.stopAnimationLoop()
      this.clearCanvas()
      if (val) {
        this.dialogType = (val.totalPv || 0) === 0 ? 'timeMachineNoPv' : null
        this.loadCaptureImage()
      } else {
        this.dialogType = null
      }
    }
  },
  created () {
    this.loadCaptureImage()
    this.generateDummyPvData()
  },
  beforeDestroy () {
    this.stopAnimationLoop()
  },
  methods: {
    getPlaybackStartTimestamp () {
      const startDate = this.reportData && this.reportData.startDate
      if (!startDate) {
        return this.frames.length ? this.frames[0].timestamp : 0
      }
      if (typeof startDate === 'number') return startDate

      const normalized = typeof startDate === 'string' && !startDate.endsWith('Z')
        ? `${startDate}Z`
        : startDate
      const timestamp = new Date(normalized).getTime()

      if (!isNaN(timestamp)) return timestamp
      return this.frames.length ? this.frames[0].timestamp : 0
    },
    toDateParam (value, fallback) {
      if (!value) return fallback
      if (typeof value === 'string') return value.slice(0, 10)
      const dateValue = value instanceof Date ? value : new Date(value)
      if (isNaN(dateValue.getTime())) return fallback
      return dateValue.toISOString().slice(0, 10)
    },
    generateDummyPvData () {
      const totalSeconds = 5 * 60
      const data = []
      for (let i = 0; i < totalSeconds; i++) {
        data.push({ activePv: Math.floor(Math.random() * 100), timestamp: null })
      }
      this.dummyPvData = data
    },
    loadCaptureImage () {
      this.isLoading = true
      const params = this.captureImageParams

      getCaptureImageUrl(params, this.captureImageTimestamp).then(imageUrl => {
        this.imageUrl = imageUrl
        this.initCaptureImageContainer(this.imageUrl)
      }).catch(err => {
        console.error(err)
        this.imageUrl = null
        this.isLoading = false
        this.$emit('image-loaded', null)
      })
    },
    initCaptureImageContainer (imageUrl) {
      if (!imageUrl) {
        this.captureWidth = 0
        this.captureHeight = 0
        this.isLoading = false
        this.setContainerSize()
      } else {
        const img = document.createElement('img')
        img.addEventListener('load', event => {
          const el = event.target
          this.captureWidth = el.width
          this.captureHeight = el.height
          this.isLoading = false
          this.setContainerSize()
          this.$emit('image-loaded', {
            url: this.imageUrl,
            width: this.captureWidth,
            height: this.captureHeight
          })
        })
        img.src = imageUrl
      }
    },
    onSegmentChange (segment) {
      if (this.device !== segment.device) {
        this.device = segment.device
        this.loadCaptureImage()
      }
      this.$emit('segment-change', segment)
    },
    onFitModeChange (mode) {
      this.fitMode = mode
      this.setContainerSize()
    },
    setContainerSize () {
      if (!this.$refs.container) return
      const elRect = this.$refs.container.getBoundingClientRect()
      let elWidth = elRect.width
      let elHeight = elRect.height
      let defaultContainerWidth
      const DEFAULT_MARGIN = 40

      if (this.device === 'phone') {
        if (this.captureWidth > elWidth) {
          defaultContainerWidth = elWidth - 302
        } else {
          defaultContainerWidth = this.captureWidth
        }
      } else {
        defaultContainerWidth = elWidth - DEFAULT_MARGIN
      }

      if (this.fitMode === 'width') {
        this.containerWidth = defaultContainerWidth
        this.containerHeight = this.ratio ? this.containerWidth / this.ratio : 0
        this.containerScale = 1
      } else {
        this.containerHeight = elHeight - DEFAULT_MARGIN
        this.containerWidth = this.ratio ? this.containerHeight * this.ratio : 0
        this.containerScale = this.containerWidth / defaultContainerWidth
      }

      this.$nextTick(() => {
        this.resizeCanvasIfNeeded()
        if (this.markers.length > 0) {
          this.redrawCanvas()
        }
      })
    },
    onFrameChange (secondIndex, speed) {
      if (speed) this.playbackSpeed = speed
      if (!this.frames.length) return
      const startTs = this.getPlaybackStartTimestamp()
      const now = Date.now()
      let added = false
      this.frames.forEach(frame => {
        const frameSec = Math.max(0, Math.floor((frame.timestamp - startTs) / 1000))
        if (frameSec !== secondIndex) return
        if (frame.heatmap && frame.heatmap.click) {
          this.addMarkersFromFrameData(frame.heatmap.click, 'click', now)
          added = true
        }
        if (frame.heatmap && frame.heatmap.move) {
          this.addMarkersFromFrameData(frame.heatmap.move, 'move', now)
          added = true
        }
        if (frame.heatmap && frame.heatmap.hover) {
          this.addMarkersFromFrameData(frame.heatmap.hover, 'hover', now)
          added = true
        }
      })
      if (added) {
        this.redrawCanvas()
        if (!this.animationFrameId) {
          this.startAnimationLoop()
        }
      }
    },
    addMarkersFromFrameData (data, type, timestamp) {
      let maxCount = 0
      Object.keys(data).forEach(x => {
        Object.keys(data[x]).forEach(y => {
          const count = data[x][y]
          if (count > maxCount) maxCount = count
        })
      })
      if (maxCount === 0) return
      Object.keys(data).forEach(x => {
        Object.keys(data[x]).forEach(y => {
          const count = data[x][y]
          this.markers.push({
            type,
            origX: Number(x),
            origY: Number(y),
            opacity: 0.3 + (count / maxCount) * 0.7,
            timestamp,
            fadeStartOpacity: undefined
          })
        })
      })
    },
    startAnimationLoop () {
      this.stopAnimationLoop()
      const animate = () => {
        this.updateAndRedraw()
        this.animationFrameId = requestAnimationFrame(animate)
      }
      animate()
    },
    stopAnimationLoop () {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId)
        this.animationFrameId = null
      }
    },
    updateAndRedraw () {
      const now = Date.now()
      const speed = this.playbackSpeed || 1
      const fadeStartTime = 2500 / speed
      const fadeDuration = 500 / speed
      const totalLifetime = fadeStartTime + fadeDuration
      let hasChanges = false

      this.markers.forEach(marker => {
        const age = now - marker.timestamp
        if (age >= totalLifetime) {
          marker.opacity = 0
          hasChanges = true
        } else if (age >= fadeStartTime) {
          if (marker.fadeStartOpacity === undefined) {
            marker.fadeStartOpacity = marker.opacity
          }
          const fadeProgress = (age - fadeStartTime) / fadeDuration
          marker.opacity = marker.fadeStartOpacity * (1 - fadeProgress)
          hasChanges = true
        }
      })

      const prevLength = this.markers.length
      this.markers = this.markers.filter(m => m.opacity > 0.001)
      if (prevLength !== this.markers.length) hasChanges = true

      if (this.markers.length === 0) {
        this.stopAnimationLoop()
        this.clearCanvas()
        return
      }
      if (hasChanges && shouldRenderFrame({
        now,
        lastRenderAt: this.lastRenderAt,
        maxFps: 30
      })) {
        this.redrawCanvas()
      }
    },
    clearCanvas () {
      const canvas = this.$refs.heatmapCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    },
    resizeCanvasIfNeeded () {
      const canvas = this.$refs.heatmapCanvas
      if (!canvas) return
      const width = Math.max(0, Math.round(this.containerWidth))
      const height = Math.max(0, Math.round(this.containerHeight))
      if (!width || !height) return
      if (canvas.width !== width) canvas.width = width
      if (canvas.height !== height) canvas.height = height
    },
    redrawCanvas () {
      const canvas = this.$refs.heatmapCanvas
      if (!canvas) return
      this.resizeCanvasIfNeeded()
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const scale = this.captureWidth ? this.containerWidth / this.captureWidth : 0
      if (!scale) return
      const scaledMarkers = this.markers.map(marker => ({
        type: marker.type,
        opacity: marker.opacity,
        x: marker.origX * scale,
        y: marker.origY * scale
      }))
      const buckets = bucketMarkersByOpacity(scaledMarkers)

      Object.keys(buckets.click).forEach(bucketKey => {
        const markers = buckets.click[bucketKey]
        ctx.save()
        ctx.globalAlpha = Number(bucketKey)
        ctx.strokeStyle = 'rgba(250, 237, 76, 1)'
        ctx.lineWidth = 3
        ctx.lineCap = 'round'
        ctx.beginPath()
        markers.forEach(marker => {
          ctx.moveTo(marker.x, marker.y - 11)
          ctx.lineTo(marker.x, marker.y + 11)
          ctx.moveTo(marker.x - 11, marker.y)
          ctx.lineTo(marker.x + 11, marker.y)
        })
        ctx.stroke()
        ctx.restore()
      })

      Object.keys(buckets.move).forEach(bucketKey => {
        const markers = buckets.move[bucketKey]
        ctx.save()
        ctx.globalAlpha = Number(bucketKey)
        ctx.fillStyle = 'rgba(250, 237, 76, 1)'
        markers.forEach(marker => {
          ctx.fillRect(marker.x - 1, marker.y - 1, 22, 22)
        })
        ctx.restore()
      })

      this.lastRenderAt = Date.now()
    },
    drawPlus (ctx, x, y, size, lineWidth, color, opacity) {
      ctx.save()
      ctx.globalAlpha = opacity
      ctx.strokeStyle = color
      ctx.lineWidth = lineWidth
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(x, y - size / 2)
      ctx.lineTo(x, y + size / 2)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(x - size / 2, y)
      ctx.lineTo(x + size / 2, y)
      ctx.stroke()
      ctx.restore()
    },
    drawSquare (ctx, x, y, width, height, lineWidth, fillColor, opacity) {
      ctx.save()
      ctx.globalAlpha = opacity
      ctx.fillStyle = fillColor
      ctx.fillRect(x - lineWidth / 2, y - lineWidth / 2, width, height)
      ctx.restore()
    }
  }
}
</script>

<style scoped lang="scss">
.heatmap-time-machine {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 0;

  .view_area_wrap {
    position: absolute;
    top: 118px;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    overflow: hidden;
    width: 100%;

    .view_area {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      overflow-y: scroll;
      background: #151515;
      width: 100%;
      height: 100%;

      &.hidden-x {
        overflow-x: hidden;
      }

      .view {
        position: relative;
        background-repeat: no-repeat;
        background-size: 100% 100%;
        margin: 20px auto;

        &::before {
          opacity: 0.7;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #282b39;
          content: "";
        }

        .view-heatmap {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          pointer-events: none;
        }
      }

      .loading_indicator {
        position: absolute;
        z-index: 100;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;

        .loading {
          position: relative;
          width: 54px;
          height: 54px;
          margin: 0 auto;
          margin-top: 200px;
        }

        svg {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          z-index: 1;
          animation: loading 3s linear infinite;

          .loading-inner {
            stroke-dashoffset: 0;
            stroke-dasharray: 300;
            stroke-width: 20;
            stroke-miterlimit: 10;
            stroke-linecap: round;
            animation: loading-circle 2s linear infinite;
            stroke: #fff;
            fill: transparent;
          }
        }

        .loading_notice {
          position: absolute;
          left: 50%;
          bottom: -27px;
          transform: translateX(-50%);
          width: 300px;
          word-break: break-all;
          text-align: center;
          font-size: 13px;
          color: #ffffff;
          font-weight: 500;
        }
      }
    }

    @keyframes loading {
      0% { transform: rotate(0); }
      100% { transform: rotate(360deg); }
    }

    @keyframes loading-circle {
      0% { stroke-dashoffset: 0; }
      100% { stroke-dashoffset: -600; }
    }

    .view_options {
      position: absolute;
      right: 25px;
      top: 25px;
      z-index: 1;

      .fitMode {
        position: relative;
        background: rgba(0, 0, 0, 0.3);
        border-radius: 3px;
        border: 1px solid rgba(0, 0, 0, 0.3);

        &::before {
          z-index: 1;
          content: "";
          display: block;
          width: 20px;
          height: 20px;
          background: #ec0047;
          box-shadow: 0 2px 4px -11px rgba(0, 0, 0, 0.25);
          border-radius: 3px;
          position: absolute;
          top: -1px;
          left: -1px;
          transition: top 0.15s ease;
        }

        &.height::before {
          top: 19px;
        }

        a {
          position: relative;
          z-index: 2;
          display: block;
          width: 20px;
          height: 20px;

          &.fit_to_width {
            margin: -1px -1px 0 -1px;
          }

          &.fit_to_height {
            margin: 0 -1px -1px -1px;
          }
        }
      }
    }
  }
}
</style>
